import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	ConfigProvider,
	Flex,
	Tabs,
} from 'antd';
import { useParams } from 'react-router';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from 'react-query';
import { splitActionKey } from '@shared/utils/splitActionKey';
import {
	CargoContractStatusTypes,
	CargoContractSides,
	LaytimeTerms,
} from '@shared/utils/constants';
import { asyncForEach } from '@shared/utils/array';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { sortCargoPorts } from '@shared/utils/sortCargoPorts';
import type { CargoDetails, GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import type { CargoPortProps } from '@api/models/cargo-port';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { UpdateCargoRequest } from '@api/features/cargos/updateCargo';
import type { UpdateCargoPortRequest } from '@api/features/cargos/updateCargoPort';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import Button from '@client/components/Button';
import { Links } from '@client/utils/links';
import {
	UpdateCargoPort,
	useCargoHandlers,
} from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import SavingIndicator from '@client/components/SavingIndicator';
import { useAuth } from '@client/lib/auth';
import { useEstimateChangesProvider } from '@client/screens/estimates/details/context/EstimateChangesContext';
import { CentralStoreProvider } from '@client/screens/estimates/details/context/CentralStoreContext';
import {
	createCargo,
	getVoyageItinerary,
	removeCargoFromContract,
	updateCargo,
	updateCargoPort,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useCargoProvider } from '@client/screens/estimates/details/context/CargoContext';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import Modal from '@client/components/Modal/Modal';
import { isSyncedCargoPortField } from '../helpers/cargoUtils';
import CargoDetailsContent from './CargoDetailsContent';
import {
	getCargoDescription,
	getLoadedAndDischarged,
	getMissingCargoFields,
	getMissingCargoPortFields,
	getRemoveCargoDisabledStr
} from './utils/CargoScreenUtil';
import { getContractReletPair } from './utils/ContractReletCargoUtils';
import CargoContractSideTag from './utils/CargoContractSideTag';

export type ModalModes = 'estimate' | 'creatingOnContract' | 'editingOnContract';

// Props used when containing CargoDetailsScreen in modal (CargoDetailsModal)
type Props = {
	mode: ModalModes;
	cargoId?: number;
	isTceLocked?: boolean;
	selectedEstimateId?: number;
	blockClose?: boolean;
	setBlockClose?: React.Dispatch<React.SetStateAction<boolean>>;
	estimate: GetEstimateDetailsResponse | null;
	voyageId?: number;
	onCargoCreation?: () => void;
	selectedCargoPortId?: number | null;
};

const SYNCED_RELET_CARGO_FIELDS: Array<
	keyof UpdateCargoRequest['attributes']
> = [
	'quantity',
	'quantityTolerance',
	'unit',
	'secondaryUnit',
	'stowageFactor',
	'loadingPorts',
	'dischargePorts',
	'laytimeTerms',
];

const CargosDetailsScreen = ({
	mode,
	cargoId,
	isTceLocked,
	blockClose,
	setBlockClose,
	selectedEstimateId,
	estimate,
	voyageId,
	onCargoCreation,
	selectedCargoPortId,
}: Props) => {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const isCargoDetailsRoute = useRouteMatch(Links.Cargo.path) != null;
	const auth = useAuth();

	const inEstimate = mode === 'estimate';
	const creatingCargoOnContract = mode === 'creatingOnContract';

	const getId = () => {
		if (inEstimate) {
			return cargoId;
		}

		if (creatingCargoOnContract) {
			return null;
		}

		if (cargoId != null) {
			return cargoId;
		}

		if (isCargoDetailsRoute && id != null) {
			return Number(id);
		}

		return null;
	};

	const [parsedCargoId, setParsedCargoId] = useState(getId());
	const [saving, setSaving] = useState(false);

	// For when users create a new cargo and add cargo ports that should be merged with existing port calls
	// on the itinerary. ONLY for new cargoes. 
	const [portIdsToMerge, setPortIdsToMerge] = useState<number[]>([]);

	// For when users add or remove a port on the cargo (left hand side)
	// and we need to possibly merge with an existing port call
	const [portInCargoChangeValues, setPortInCargoChangeValues] = useState<{
		cargoId: number;
		attributes: UpdateCargoRequest['attributes'];
	}>();

	// For when users change the port of a cargo port (edit right hand side)
	// and we need to possibly merge with an existing port call
	const [portInCargoPortChangeValues, setPortInCargoPortChangeValues] = useState<{
		cargoPortId: number;
		cargoId: number;
		attributes: UpdateCargoPortRequest['attributes'];
	}>();

	const [renderKey, setRenderKey] = useState(0);

	// Allows revert to previous state of values, in case user tries to add
	// a new port, is prompted for a decision on whether or not to merge said port,
	// but then regrets and closes the modal
	const forceRender = () => setRenderKey((prev) => prev + 1);

	const {
		cargo,
		cargos: contractCargos,
		cargoPorts: cargoCargoPorts,
		onUpdateCargo,
		onUpdateCargoPort,
		onDeleteCargoMutator,
		copyCargoPortSettings,
		hasPendingChanges,
		pendingCargoChanges,
		pendingCargoPortChanges,
		setPendingCargoChanges,
		setPendingCargoPortChanges,
		hardRefresh,
	} = useCargoHandlers({
		cargoId: parsedCargoId,
		selectedEstimateId: selectedEstimateId ?? null,
		creatingCargoOnContract,
		handleCargoIdChange: (id: number) => {
			setParsedCargoId(id);
		}
	});

	const voyageIdToUse = useMemo(() => voyageId ?? cargo?.voyageId, [cargo, voyageId]);

	const {
		cargos,
		cargoPorts: estimateCargoPorts,
		onUpdateCargo: onUpdateEstimateCargo,
		onUpdateCargoPort: onUpdateEstimateCargoPort,
		copyCargoPortSettings: copyEstimateCargoPortSettings,
	} = useCargoProvider() ?? {};

	const {
		saving: estimateSaving,
		onSaveChanges,
		hasPendingChanges: hasPendingChangesInEstimate,
	} = useEstimateChangesProvider() ?? {};

	const {
		data: itinerary,
		refetch: refetchItinerary,
	} = useQuery(
		['itinerary', voyageIdToUse],
		() => getVoyageItinerary(voyageIdToUse!),
		{
			enabled: voyageIdToUse != null,
			initialData: [],
		},
	);

	const pendingChangesToUse = inEstimate ? hasPendingChangesInEstimate : hasPendingChanges;
	const copyCargoPortSettingsToUse = inEstimate ? copyEstimateCargoPortSettings : copyCargoPortSettings;
	const savingToUse = inEstimate ? estimateSaving : saving;

	const cargoDetails = useMemo(() => {
		if (inEstimate) {
			return cargos.find((c) => c.id === parsedCargoId);
		}

		return cargo;
	}, [cargo, inEstimate, cargos, parsedCargoId]);

	const allCargos = useMemo(() => (
		inEstimate ? (cargos ?? []) : (contractCargos ?? [])
	), [inEstimate, cargos, contractCargos]);

	const {
		vcOutCargo,
		vcInCargo,
		hasCargoRelet,
	} = getContractReletPair({
		cargoDetails,
		cargos: allCargos,
	});

	const activeTabKey = String(parsedCargoId);

	const handleTabChange = useCallback((key: string) => {
		const nextCargoId = Number(key);
		setParsedCargoId(nextCargoId);
		if (!inEstimate) {
			history.replace(Links.Cargo.get(nextCargoId));
		}
	}, [history, inEstimate]);

	let baseCurrency;

	if (inEstimate && estimate != null) {
		baseCurrency = estimate.currency;
	} else if (!inEstimate && cargoDetails?.estimateCurrency != null) {
		baseCurrency = cargoDetails.estimateCurrency;
	} else {
		baseCurrency = auth.userInfo.baseCurrency;
	}

	const cargoPorts = useMemo(() => {
		if (inEstimate) {
			return (estimateCargoPorts ?? []).filter((cp) => cp.cargoId === parsedCargoId);
		}

		return cargoCargoPorts ?? [];
	}, [inEstimate, parsedCargoId, estimateCargoPorts, cargoCargoPorts]);

	useEffect(() => {
		const nextId = getId();

		if (nextId != null) {
			setParsedCargoId(nextId);
		}
	}, [cargoId, id, inEstimate, creatingCargoOnContract, isCargoDetailsRoute]);

	const onSaveInCargo = useCallback(async () => {
		setSaving(true);

		let createdCargoId: number | null = null;

		try {
			if (pendingCargoChanges.length > 0) {
				await asyncForEach(pendingCargoChanges, async (c) => {
					if (c.cargoId === -1) {
						const loadingPortsToCreate: Partial<CargoPortProps>[] = [];
						const dischargePortsToCreate: Partial<CargoPortProps>[] = [];

						c.attributes?.loadingPorts?.forEach((lp) => {
							const pendingChange = pendingCargoPortChanges.find((cp) => cp.attributes?.port?.id === lp);

							if (pendingChange) {
								loadingPortsToCreate.push({
									...pendingChange.attributes,
									cpQuantity: pendingChange.attributes.cpQuantity ?? cargo.quantity,
								});
							}
						});

						c.attributes?.dischargePorts?.forEach((dp) => {
							const pendingChange = pendingCargoPortChanges.find((cp) => cp.attributes?.port?.id === dp);

							if (pendingChange) {
								dischargePortsToCreate.push({
									...pendingChange.attributes,
									cpQuantity: pendingChange.attributes.cpQuantity ?? cargo.quantity,
								});
							}
						});

						const createdCargo = await createCargo({
							attributes: {
								...c.attributes,
								portIdsToMerge,
								loadPortsToCreate: loadingPortsToCreate,
								dischargePortsToCreate: dischargePortsToCreate,
								contractStatus: CargoContractStatusTypes.OnVoyage,
							}, voyageId,
						});

						showSuccessNotification(
							'New cargo ports added - remember to add port DAs under "Expenses"',
							(
								<a href={Links.Voyage.Expenses.get(createdCargo.voyageId!)}>
									Go to expenses
								</a>
							),
							20,
						);

						setParsedCargoId(createdCargo.id);
						createdCargoId = createdCargo.id;
						onCargoCreation?.();

						return;
					}

					await updateCargo(c, false);
				});
			}

			if (pendingCargoPortChanges.length > 0) {
				await asyncForEach(pendingCargoPortChanges, async (c) => {
					if (!creatingCargoOnContract) {
						await updateCargoPort(c, false);
					}
				});
			}

			setPendingCargoPortChanges([]);
			setPendingCargoChanges([]);

			const idToRefresh = createdCargoId ?? parsedCargoId;
			await hardRefresh(idToRefresh);
		} catch (e) {
			showErrorNotification('Could not update cargo', e as Error);
		} finally {
			setSaving(false);
		}
	}, [
		hardRefresh,
		portIdsToMerge,
		pendingCargoChanges,
		pendingCargoPortChanges,
		setPendingCargoChanges,
		setPendingCargoPortChanges,
	]);

	const syncCargoToVcIn = useCallback((
		sourceCargoId: number,
		attributes: UpdateCargoRequest['attributes'],
	) => {
		const updateCargoMutator = inEstimate ?
			onUpdateEstimateCargo :
			onUpdateCargo;

		if (updateCargoMutator == null) {
			return;
		}

		const sourceCargo = allCargos.find((c) => c.id === sourceCargoId);

		if (sourceCargo?.contractSide !== CargoContractSides.VC_OUT) {
			return;
		}

		const linkedVcInCargo = allCargos.find((c) => (
			c.contractSide === CargoContractSides.VC_IN &&
			c.reletCargoId === sourceCargo.id
		));

		if (linkedVcInCargo == null) {
			return;
		}

		const syncedAttributes = SYNCED_RELET_CARGO_FIELDS.reduce(
			(result, field) => {
				const value = attributes[field];

				if (value !== undefined) {
					Object.assign(result, {
						[field]: value,
					});
				}

				return result;
			},
			{} as UpdateCargoRequest['attributes'],
		);

		if (Object.keys(syncedAttributes).length === 0) {
			return;
		}

		updateCargoMutator(
			linkedVcInCargo.id,
			syncedAttributes,
		);
	}, [
		allCargos,
		inEstimate,
		onUpdateCargo,
		onUpdateEstimateCargo,
	]);

	const saveChanges = useCallback(async () => {
		if (inEstimate) {
			await onSaveChanges();
		} else {
			await onSaveInCargo();
		}
	}, [inEstimate, onSaveChanges, onSaveInCargo]);

	const handleCargoChange = useCallback(async (
		relevantCargoId: number,
		attributes: UpdateCargoRequest['attributes'],
	) => {
		const updateCargoInState = inEstimate ?
			onUpdateEstimateCargo :
			onUpdateCargo;

		if (updateCargoInState == null) {
			return;
		}

		const portChange = (
			attributes.loadingPorts != null ||
			attributes.dischargePorts != null
		);
		updateCargoInState(relevantCargoId, attributes);
		syncCargoToVcIn(relevantCargoId, attributes);
		if (inEstimate) {
			return;
		}

		if (!portChange) {
			return;
		}

		if (portChange) {
			const portCalls = itinerary
				?.filter(isItemPortCall)
				.slice(1) as Array<ItineraryPortCallDto>;
			const existingPortIds = portCalls?.map((pc) => pc.port.id);

			const allExistingLoadPorts = cargo.loadingPorts ?? [];
			const allExistingDischargePorts = cargo.dischargePorts ?? [];
			const existingLoadPortIds = allExistingLoadPorts.map((lp) => (typeof lp === 'number' ? lp : lp.id));
			const existingDischargePortIds = allExistingDischargePorts.map((dp) => (typeof dp === 'number' ? dp : dp.id));

			const loadIds = (attributes.loadingPorts ?? [])
				.map((lp) => (typeof lp === 'number' ? lp : lp.id))
				.filter((lp) => !existingLoadPortIds.includes(lp));
			const dischargeIds = (attributes.dischargePorts ?? [])
				.map((dp) => (typeof dp === 'number' ? dp : dp.id))
				.filter((dp) => !existingDischargePortIds.includes(dp));
			const isAdding = (
				allExistingLoadPorts.length < (attributes.loadingPorts?.length ?? 0) ||
				allExistingDischargePorts.length < (attributes.dischargePorts?.length ?? 0)
			);
			const allPortIds = [...loadIds, ...dischargeIds];
			const shouldAskForMerge = existingPortIds.some((portId) => allPortIds.includes(portId));

			if (shouldAskForMerge) {
				setPortInCargoChangeValues({
					cargoId: relevantCargoId,
					attributes,
				});
			} else {
				try {
					if (relevantCargoId === -1) {
						onUpdateCargo(relevantCargoId, attributes);
					} else {
						await updateCargo({
							cargoId: relevantCargoId,
							attributes,
						});

						const sourceCargo = allCargos.find((c) => c.id === relevantCargoId);
						const isVcOutRelet = sourceCargo?.contractSide === CargoContractSides.VC_OUT;

						if (sourceCargo?.contractSide === CargoContractSides.VC_OUT) {
							const linkedVcInCargo = allCargos.find((c) => (
								c.contractSide === CargoContractSides.VC_IN &&
								(
									c.reletCargoId === sourceCargo.id ||
									sourceCargo.reletCargoId === c.id
								)
							));

							if (linkedVcInCargo != null) {
								await updateCargo({
									cargoId: linkedVcInCargo.id,
									attributes: {
										loadingPorts: attributes.loadingPorts,
										dischargePorts: attributes.dischargePorts,
									},
									mergeNewPorts: isVcOutRelet ? true : undefined,
								});
							}
						}
					}

					if (isAdding && !creatingCargoOnContract) {
						showSuccessNotification(
							'New cargo ports added - remember to add port DAs under "Expenses"',
							(
								<a href={Links.Voyage.Expenses.get(cargo.voyageId!)}>
									Go to expenses
								</a>
							),
							20,
						);
					}
				} catch (e) {
					showErrorNotification('Something went wrong', e as Error);
					forceRender();
				} finally {
					await refetchItinerary();

					const hasPendingCargoChangesForThisCargo =
						pendingCargoChanges.some(
							(change) => change.cargoId === relevantCargoId
						);

					if (
						!creatingCargoOnContract &&
						relevantCargoId > 0 &&
						!hasPendingCargoChangesForThisCargo
					) {
						await hardRefresh(relevantCargoId);
					}
				}
			}
		} else {
			return;
		}
	}, [
		inEstimate,
		creatingCargoOnContract,
		syncCargoToVcIn,
		cargo?.dischargePorts,
		cargo?.loadingPorts,
		cargo?.voyageId,
		hardRefresh,
		itinerary,
		onUpdateCargo,
		onUpdateEstimateCargo,
		refetchItinerary,
	]);

	const getCargoPortsForCargo = useCallback((targetCargoId?: number) => {
		if (targetCargoId == null) {
			return [];
		}

		const portsToUse = inEstimate ?
			estimateCargoPorts :
			cargoCargoPorts ?? [];

		return portsToUse.filter((cp) => cp.cargoId === targetCargoId);
	}, [inEstimate, estimateCargoPorts, cargoCargoPorts]);

	const getSortedCargoPortsForCargo = useCallback((
		targetCargoId?: number,
		sortLikeCargoId?: number,
	) => {
		const portsToUse = getCargoPortsForCargo(targetCargoId);
		const sortReferencePorts = sortLikeCargoId == null ?
			portsToUse :
			getCargoPortsForCargo(sortLikeCargoId);
		const sortedReferencePorts = sortCargoPorts([...sortReferencePorts]);

		return sortedReferencePorts
			.map((referencePort) => {
				const referenceKey = splitActionKey(referencePort.portAndActionKey);

				return portsToUse.find((cp) => {
					const key = splitActionKey(cp.portAndActionKey);

					return (
						cp.port.id === referencePort.port.id &&
						key.action === referenceKey.action &&
						key.index === referenceKey.index
					);
				});
			})
			.filter((cp): cp is NonNullable<typeof cp> => cp != null)
			.map((cp) => ({
				...cp,
				key: cp.id,
			}));
	}, [getCargoPortsForCargo]);

	const handleCargoPortChange = useCallback(async (
		values: {
			cargoPortId: number;
			cargoId: number;
			attributes: UpdateCargoPort['attributes']
		},
	) => {
		const updateCargoPortInState = inEstimate ?
			onUpdateEstimateCargoPort :
			onUpdateCargoPort;

		const fieldNames = Object.keys(values.attributes)
			.filter((key) => key !== 'port') as Array<keyof UpdateCargoPort['attributes']>;

		const shouldSyncToVcIn =
			fieldNames.some((fieldName) => isSyncedCargoPortField(fieldName));

		if (shouldSyncToVcIn) {
			const sourceCargo = allCargos.find((c) => c.id === values.cargoId);

			if (sourceCargo?.contractSide === CargoContractSides.VC_OUT) {
				const linkedVcInCargo = allCargos.find((c) => (
					c.contractSide === CargoContractSides.VC_IN &&
					(
						c.reletCargoId === sourceCargo.id ||
						sourceCargo.reletCargoId === c.id
					)
				));
				const sourcePorts = getSortedCargoPortsForCargo(sourceCargo.id);
				const targetPorts = getSortedCargoPortsForCargo(
					linkedVcInCargo?.id,
					sourceCargo.id,
				);
				const sourceIndex = sourcePorts.findIndex((cp) => (
					cp.id === values.cargoPortId
				));
				const linkedVcInCargoPort = sourceIndex >= 0 ?
					targetPorts[sourceIndex] :
					undefined;

				if (linkedVcInCargoPort != null) {
					updateCargoPortInState({
						cargoId: linkedVcInCargoPort.cargoId,
						cargoPortId: linkedVcInCargoPort.id,
						attributes: values.attributes,
					});
				}
			}
		}

		const cp = cargoPorts?.find((cp) => cp.id === values.cargoPortId);
		const hasPortChange = cp && values.attributes.port && cp.port.id !== values.attributes.port.id;

		if (hasPortChange) {
			const { port } = values.attributes;

			const portCalls = itinerary
				?.filter(isItemPortCall)
				.slice(1) as Array<ItineraryPortCallDto>;

			const existingPortIds = portCalls?.map((pc) => pc.port.id);

			if (existingPortIds.includes(port.id)) {
				setPortInCargoPortChangeValues(values);
			} else {
				try {
					await updateCargoPort(values);
				} catch (e) {
					showErrorNotification('Something went wrong', e as Error);
					forceRender();
				} finally {
					await hardRefresh();
				}
			}
		} else {
			updateCargoPortInState(values);
		}
	}, [
		allCargos,
		cargoPorts,
		getSortedCargoPortsForCargo,
		hardRefresh,
		inEstimate,
		itinerary,
		onUpdateCargoPort,
		onUpdateEstimateCargoPort,
	]);

	const showMergePopup = useMemo(
		() => portInCargoChangeValues != null || portInCargoPortChangeValues != null,
		[portInCargoChangeValues, portInCargoPortChangeValues],
	);

	const resetPortMergeValues = useCallback(async () => {
		setPortInCargoChangeValues(undefined);
		setPortInCargoPortChangeValues(undefined);
		if (!creatingCargoOnContract) {
			forceRender();
		}
	}, [creatingCargoOnContract]);

	const finishPortInCargoUpdate = useCallback(async (merge: boolean) => {
		if (!portInCargoChangeValues) {
			return;
		}

		if (!creatingCargoOnContract) {
			await updateCargo({ ...portInCargoChangeValues, mergeNewPorts: merge });
			resetPortMergeValues();
			await hardRefresh();
		} else {
			const ports = [
				...(portInCargoChangeValues?.attributes?.loadingPorts ?? []),
				...(portInCargoChangeValues?.attributes?.dischargePorts ?? []),
			];

			if (merge) {
				setPortIdsToMerge((prev) => {
					const combined = [...prev, ...ports as number[]];

					return Array.from(new Set(combined));
				});
			}

			onUpdateCargo(portInCargoChangeValues.cargoId, portInCargoChangeValues.attributes);
			resetPortMergeValues();
		}
	}, [hardRefresh, portInCargoChangeValues, resetPortMergeValues]);

	const finishPortInCargoPortUpdate = useCallback(async (merge: boolean) => {
		if (!portInCargoPortChangeValues) {
			return;
		}

		await updateCargoPort({
			...portInCargoPortChangeValues,
			mergePortInItinerary: merge,
		});
		resetPortMergeValues();
		await hardRefresh();
	}, [hardRefresh, portInCargoPortChangeValues, resetPortMergeValues]);

	const removeCargoFromContractMutator = useMutation(
		removeCargoFromContract,
		{
			onSuccess: () => {
				history.goBack();
			}
		}
	);

	const removeCargo = async () => {
		if (!parsedCargoId) {
			return;
		}

		removeCargoFromContractMutator.mutate(parsedCargoId);
	};

	useEffect(() => {
		setBlockClose?.(pendingChangesToUse);
	}, [pendingChangesToUse, blockClose, setBlockClose]);

	const getLaytimeTermsForCargo = (cargo?: CargoDetails | GetCargoDetailsResponse) => {
		if (cargo?.id == null) {
			return LaytimeTerms.NON_REVERSIBLE;
		}

		const pendingChange = pendingCargoChanges.find((change) => change.cargoId === cargo.id);

		return pendingChange?.attributes.laytimeTerms ??
			cargo.laytimeTerms ??
			LaytimeTerms.NON_REVERSIBLE;
	};

	const { loaded, discharged } = useMemo(() => {
		if (cargoDetails != null && cargoDetails.cargoPorts != null) {
			return getLoadedAndDischarged(cargoDetails.cargoPorts);
		}

		return {
			loaded: 0,
			discharged: 0,
		};
	}, [cargoDetails]);

	const quantitiesRemaining = useMemo(() => {
		if (cargoDetails == null) {
			return null;
		}

		const loadedRemaining = cargoDetails.quantity - loaded;
		const dischargeRemaining = loaded - discharged;

		return {
			loadedRemaining,
			dischargeRemaining,
		};
	}, [cargoDetails, loaded, discharged]);

	const loadingPorts = useMemo(() => {
		if (cargoPorts == null) {
			return [];
		}

		return cargoPorts
			.filter((cp) => {
				const { action } = splitActionKey(cp.portAndActionKey);

				return action === 'loading';
			}).map((cp) => ({
				...cp,
				key: cp.id,
			}));
	}, [cargoPorts]);

	const dischargePorts = useMemo(() => {
		if (cargoPorts == null) {
			return [];
		}

		return cargoPorts
			.filter((cp) => {
				const { action } = splitActionKey(cp.portAndActionKey);

				return action === 'discharging';
			})
			.map((cp) => ({
				...cp,
				key: cp.id,
			}));
	}, [cargoPorts]);

	const disabledTooltip = useMemo(() => {
		if (!creatingCargoOnContract || cargo == null) {
			return null;
		}

		const missingOnCargo = getMissingCargoFields(cargo);
		const missingOnCargoPorts = cargoPorts?.map(getMissingCargoPortFields) ?? [];

		const allMissingCargoPortFields = missingOnCargoPorts.flatMap((c) => c.missingOnCargoPort);

		if (missingOnCargo.length === 0 && allMissingCargoPortFields.length === 0) {
			return null;
		}

		const cpContent = missingOnCargoPorts
			.filter((cp) => cp.missingOnCargoPort.length > 0)
			.map((cp, i) => {
				const values = cp.missingOnCargoPort
					.map((c) => (<p style={{ padding: 0, margin: 0 }} key={`${i}_${cp.portName}_portValues`}>• {c}</p>));

				return (
					<div>
						<p style={{ padding: 0, margin: 0 }} key={`${i}_${cp.portName}`}>{cp.portName}</p>
						{values}
					</div>
				);
			});

		return (
			<Flex vertical gap={10}>
				{missingOnCargo.length > 0 && (
					<div>
						<p style={{ padding: 0, margin: 0 }}>Field(s) required:</p>
						{missingOnCargo.map((s, i) => (
							<p style={{ padding: 0, margin: 0 }} key={i}>• {s}</p>
						))}
					</div>
				)}
				{missingOnCargoPorts.length > 0 && (
					<div>
						<p style={{ padding: 0, margin: 0 }}>Cargo port field(s) required:</p>
						<Flex gap={10} vertical>
							{cpContent}
						</Flex>
					</div>
				)}
			</Flex>
		);
	}, [cargo, cargoPorts]);

	const saveButtonDisabled = useMemo(() => {
		if (creatingCargoOnContract && disabledTooltip == null) {
			return false;
		}

		if (creatingCargoOnContract) {
			return true;
		}

		if (!pendingChangesToUse || savingToUse) {
			return true;
		}

		return false;
	}, [creatingCargoOnContract, pendingChangesToUse, savingToUse, disabledTooltip]);

	const deleteButtonContent = useMemo(() => {
		if (creatingCargoOnContract) {
			return null;
		}

		if (cargo?.voyageId) {
			const disabledStr = getRemoveCargoDisabledStr(cargo);
			let warning: React.ReactElement | null = null;

			if (disabledStr.length > 0) {
				warning = (
					<Flex vertical>
						<p style={{ margin: 0, padding: 0 }}>Cannot remove cargo:</p>
						{disabledStr.map((s) => (
							<p style={{ margin: 0, padding: 0 }}>• {s}</p>
						))}
					</Flex>
				);
			}

			return (
				<Button
					danger
					confirmTitle="Do you also want to delete the cargo entirely, or only remove it from the contract?"
					popconfirmProps={{
						okText: 'Remove & delete cargo',
						cancelText: 'Remove cargo',
						onCancel: async () => {
							await removeCargo();
						},
						okButtonProps: {
							danger: true,
						},
					}}
					disabled={disabledStr.length > 0 || hasCargoRelet}
					disabledTooltip={warning}
					onClick={async () => {
						await removeCargo();

						const idToUse = cargoId ?? parsedCargoId;

						if (!idToUse) {
							return;
						}

						onDeleteCargoMutator.mutate(idToUse);
					}}
				>
					Remove cargo from contract
				</Button>
			);
		}

		return (
			<Button
				danger
				confirmTitle="Are you sure you want to delete this cargo?"
				onClick={() => {
					const idToUse = cargoId ?? parsedCargoId;

					if (!idToUse) {
						return;
					}

					onDeleteCargoMutator.mutate(idToUse, {
						onSuccess: () => {
							history.goBack();
						},
					});
				}}
			>
				Delete Cargo
			</Button>
		);
	}, [cargo, creatingCargoOnContract, cargoId, parsedCargoId, removeCargo]);

	const sortedCargoPorts = [
		...loadingPorts.sort((a, b) => a.id - b.id),
		...dischargePorts.sort((a, b) => a.id - b.id),
	];

	const content = hasCargoRelet && vcOutCargo != null && vcInCargo != null ? (
		<Tabs
			activeKey={activeTabKey}
			onChange={handleTabChange}
			items={[
				{
					key: String(vcOutCargo.id),
					label:
						<CargoContractSideTag
							contractSide={CargoContractSides.VC_OUT}
							chartererName={vcOutCargo.chartererName}
						/>,
					children: (
						<CargoDetailsContent
							mode={mode}
							isTceLocked={isTceLocked}
							estimate={estimate}
							selectedCargoPortId={selectedCargoPortId}
							inEstimate={inEstimate}
							renderKey={renderKey}
							cargoDetails={vcOutCargo}
							baseCurrency={baseCurrency}
							laytimeTerms={getLaytimeTermsForCargo(vcOutCargo)}
							cargoPorts={getCargoPortsForCargo(vcOutCargo.id)}
							sortedCargoPorts={getSortedCargoPortsForCargo(vcOutCargo.id)}
							quantitiesRemaining={quantitiesRemaining}
							handleCargoChange={handleCargoChange}
							handleCargoPortChange={handleCargoPortChange}
							onUpdateEstimateCargo={onUpdateEstimateCargo}
							onUpdateEstimateCargoPort={onUpdateEstimateCargoPort}
							onUpdateCargo={onUpdateCargo}
							copyCargoPortSettings={copyCargoPortSettingsToUse}
						/>
					),
				},
				{
					key: String(vcInCargo.id),
					label:
						<CargoContractSideTag
							contractSide={CargoContractSides.VC_IN}
							chartererName={vcInCargo.chartererName}
						/>,
					children: (
						<CargoDetailsContent
							mode={mode}
							isTceLocked={isTceLocked}
							estimate={estimate}
							selectedCargoPortId={selectedCargoPortId}
							inEstimate={inEstimate}
							renderKey={renderKey}
							cargoDetails={vcInCargo}
							baseCurrency={baseCurrency}
							laytimeTerms={getLaytimeTermsForCargo(vcInCargo)}
							cargoPorts={getCargoPortsForCargo(vcInCargo.id)}
							sortedCargoPorts={getSortedCargoPortsForCargo(vcInCargo.id, vcOutCargo.id)}
							quantitiesRemaining={quantitiesRemaining}
							handleCargoChange={handleCargoChange}
							handleCargoPortChange={handleCargoPortChange}
							onUpdateEstimateCargo={onUpdateEstimateCargo}
							onUpdateEstimateCargoPort={onUpdateEstimateCargoPort}
							onUpdateCargo={onUpdateCargo}
							copyCargoPortSettings={copyCargoPortSettingsToUse}
						/>
					),
				},
			]}
		/>
	) : (
		<CargoDetailsContent
			mode={mode}
			isTceLocked={isTceLocked}
			estimate={estimate}
			selectedCargoPortId={selectedCargoPortId}
			inEstimate={inEstimate}
			renderKey={renderKey}
			cargoDetails={cargoDetails}
			baseCurrency={baseCurrency}
			laytimeTerms={getLaytimeTermsForCargo(cargoDetails)}
			cargoPorts={cargoPorts}
			sortedCargoPorts={sortedCargoPorts}
			quantitiesRemaining={quantitiesRemaining}
			handleCargoChange={handleCargoChange}
			handleCargoPortChange={handleCargoPortChange}
			onUpdateEstimateCargo={onUpdateEstimateCargo}
			onUpdateEstimateCargoPort={onUpdateEstimateCargoPort}
			onUpdateCargo={onUpdateCargo}
			copyCargoPortSettings={copyCargoPortSettingsToUse}
		/>
	);

	return (
		<CentralStoreProvider>
			<ConfigProvider componentDisabled={savingToUse}>
				<SimpleScreen
					reducedPadding
					title={getCargoDescription(cargoDetails)}
					breadcrumbs={
						[[
							'Cargos',
							Links.Cargos.get(),
						]]
					}
					headerActions={[
						(
							<Flex gap={10}>
								<SavingIndicator spinning={savingToUse} />
								<Button
									onClick={saveChanges}
									disabled={saveButtonDisabled || (creatingCargoOnContract && hasCargoRelet)}
									disabledTooltip={disabledTooltip}
									type="primary"
									icon={(<SaveOutlined />)}
									pulse={!saveButtonDisabled}
									data-testid="save-cargo-btn"
									tooltipProps={{
										placement: 'bottom'
									}}
								>
									{creatingCargoOnContract ? 'Add cargo to contract' : 'Save'}
								</Button>
								{deleteButtonContent}
							</Flex>
						),
					]}
					canGoBack
					rootPageTitle="Cargos"
				>
					<Modal
						centered
						open={showMergePopup}
						closable
						onCancel={resetPortMergeValues}
						okButtonProps={{
							style: {
								visibility: 'hidden',
							},
						}}
						cancelButtonProps={{
							style: {
								visibility: 'hidden',
							},
						}}
						footer={null}
					>
						<Flex vertical gap={10}>
							<p>
								There&apos;s another itinerary entry with this port -
								do you want to merge these entries or keep the port calls separate?
							</p>
							<Flex gap={10}>
								<Button
									type="primary"
									onClick={() => {
										if (portInCargoChangeValues) {
											finishPortInCargoUpdate(true);

											return;
										}

										finishPortInCargoPortUpdate(true);
									}}
								>
									Merge
								</Button>
								<Button
									onClick={() => {
										if (portInCargoChangeValues) {
											finishPortInCargoUpdate(false);

											return;
										}

										finishPortInCargoPortUpdate(false);
									}}
								>
									Keep separate
								</Button>
							</Flex>
						</Flex>
					</Modal>
					{content}
				</SimpleScreen>
			</ConfigProvider>
		</CentralStoreProvider>
	);
};

export default CargosDetailsScreen;
